/**
 * useGpsController — Iter 36.5 Viewer Architecture Hardening.
 *
 * Owns ALL GPS state + behavior for SheetViewer:
 *   - calibration (via useGpsCalibration)
 *   - live position (via useLivePosition, gated on gpsEnabled)
 *   - calibration transform + ready flag
 *   - Locate Me one-tap action + pending-locate latch
 *   - Follow Mode (auto-recenter + 2s re-poll)
 *   - Drop Mode (one-shot manual marker)
 *   - manualPosition state
 *   - awaitingAnchorTap + anchorTapPos (calibration anchor placement flow)
 *   - distanceFromMe(nx, ny) helper used by Inspector
 *
 * Pure extraction — identical semantics to the previous inline SheetViewer
 * code. This is the seam future GPS upgrades (calibration history UI,
 * project-wide inheritance, stationing alignment) will plug into.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { dispatchCanvasCmd } from "@/components/SheetCanvas";
import { useGpsCalibration, useLivePosition } from "@/components/GpsOverlay";
import { buildCalibration, haversineMeters } from "@/lib/gpsCalibration";


export function useGpsController({ sheetId }) {
  const [gpsPanelOpen, setGpsPanelOpen] = useState(false);
  const [gpsEnabled, setGpsEnabled] = useState(() => {
    try { return localStorage.getItem("fop_gps_enabled") === "1"; } catch { return false; }
  });
  const [manualPosition, setManualPosition] = useState(null); // {nx, ny}
  const [awaitingAnchorTap, setAwaitingAnchorTap] = useState(false);
  const [anchorTapPos, setAnchorTapPos] = useState(null); // {nx, ny}

  const { cal, save: saveCalibration, clear: clearCalibration } = useGpsCalibration(sheetId);
  const { pos: livePosition, error: liveError } = useLivePosition(gpsEnabled);

  // Iter 34 — Locate / Follow / Drop. Both clear cleanly on sheet change
  // so the user never inherits a stale follow/drop state.
  const [followMode, setFollowMode] = useState(false);
  const [dropMode, setDropMode] = useState(false);
  useEffect(() => { setFollowMode(false); setDropMode(false); }, [sheetId]);

  // GPS calibration transform — memoized so it doesn't rebuild on every render.
  const gpsTransform = useMemo(() => {
    const built = buildCalibration(cal?.points || []);
    return built.ok ? built.transform : null;
  }, [cal?.points]);

  // Iter 34 — derived "GPS ready" flag (≥2 anchors + valid transform).
  const gpsReady = !!gpsTransform;

  const toggleGpsLive = useCallback(() => {
    setGpsEnabled((v) => {
      const next = !v;
      try { localStorage.setItem("fop_gps_enabled", next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  // Locate Me — one-tap. Uncalibrated → surfaces panel; otherwise enables
  // GPS if off and pans to position. When no fix has arrived yet, the
  // camera pans on the FIRST sample via the pendingLocate effect below.
  const [pendingLocate, setPendingLocate] = useState(false);
  const handleLocateMe = useCallback(() => {
    if (!gpsReady) {
      setGpsPanelOpen(true);
      return;
    }
    if (!gpsEnabled) {
      setGpsEnabled(true);
      try { localStorage.setItem("fop_gps_enabled", "1"); } catch {/* ignore */}
      setPendingLocate(true);
      return;
    }
    if (livePosition && gpsTransform) {
      const xy = gpsTransform.gpsToSheet(livePosition.lat, livePosition.lng);
      if (xy && xy.nx >= 0 && xy.nx <= 1 && xy.ny >= 0 && xy.ny <= 1) {
        dispatchCanvasCmd(sheetId, "centerOn", { nx: xy.nx, ny: xy.ny });
      }
    } else {
      setPendingLocate(true);
    }
  }, [gpsReady, gpsEnabled, gpsTransform, livePosition, sheetId]);

  // Pending Locate Me — fires once when the first fix arrives.
  useEffect(() => {
    if (!pendingLocate || !livePosition || !gpsTransform) return;
    const xy = gpsTransform.gpsToSheet(livePosition.lat, livePosition.lng);
    if (xy && xy.nx >= 0 && xy.nx <= 1 && xy.ny >= 0 && xy.ny <= 1) {
      dispatchCanvasCmd(sheetId, "centerOn", { nx: xy.nx, ny: xy.ny });
    }
    setPendingLocate(false);
  }, [pendingLocate, livePosition, gpsTransform, sheetId]);

  // Follow Mode — keep camera centered as livePosition updates.
  // centerOn preserves zoom by contract.
  useEffect(() => {
    if (!followMode || !livePosition || !gpsTransform) return;
    const xy = gpsTransform.gpsToSheet(livePosition.lat, livePosition.lng);
    if (xy && xy.nx >= 0 && xy.nx <= 1 && xy.ny >= 0 && xy.ny <= 1) {
      dispatchCanvasCmd(sheetId, "centerOn", { nx: xy.nx, ny: xy.ny });
    }
  }, [followMode, livePosition, gpsTransform, sheetId]);

  // 2s re-poll alongside watchPosition while Follow is on. Guarantees
  // liveness on iPad Safari battery-saver throttling AND synthetic
  // geolocation in test/dev tools that only fires on demand.
  useEffect(() => {
    if (!followMode || !gpsTransform || typeof navigator === "undefined" || !navigator.geolocation) return;
    let cancelled = false;
    const tick = () => {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          if (cancelled || !gpsTransform) return;
          const xy = gpsTransform.gpsToSheet(p.coords.latitude, p.coords.longitude);
          if (xy && xy.nx >= 0 && xy.nx <= 1 && xy.ny >= 0 && xy.ny <= 1) {
            dispatchCanvasCmd(sheetId, "centerOn", { nx: xy.nx, ny: xy.ny });
          }
        },
        () => {/* swallow — watchPosition surfaces real errors */},
        { enableHighAccuracy: true, maximumAge: 0, timeout: 8000 },
      );
    };
    tick();
    const iv = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [followMode, gpsTransform, sheetId]);

  // Auto-enable GPS when Follow Mode is engaged.
  const handleToggleFollow = useCallback(() => {
    setFollowMode((v) => {
      const next = !v;
      if (next && gpsReady && !gpsEnabled) {
        setGpsEnabled(true);
        try { localStorage.setItem("fop_gps_enabled", "1"); } catch {/* ignore */}
      }
      return next;
    });
  }, [gpsReady, gpsEnabled]);

  const handleToggleDropMode = useCallback(() => {
    setDropMode((v) => !v);
  }, []);

  // Iter 34 — "Distance from me" helper. Given normalized (nx,ny), returns
  // haversine distance to the current GPS fix in feet, or null when GPS
  // isn't usable. Used by Inspector to render the DistanceFromMe sub-panel.
  const distanceFromMe = useCallback((nx, ny) => {
    if (!livePosition || !gpsTransform) return null;
    const target = gpsTransform.sheetToGps(nx, ny);
    if (!target) return null;
    const meters = haversineMeters(livePosition.lat, livePosition.lng, target.lat, target.lng);
    return meters * 3.28084; // → feet
  }, [livePosition, gpsTransform]);

  return {
    // Calibration
    cal,
    saveCalibration,
    clearCalibration,
    gpsTransform,
    gpsReady,
    // Live state
    livePosition,
    liveError,
    gpsEnabled,
    toggleGpsLive,
    // Panel + mode flags
    gpsPanelOpen,
    setGpsPanelOpen,
    followMode,
    dropMode,
    manualPosition,
    setManualPosition,
    awaitingAnchorTap,
    setAwaitingAnchorTap,
    anchorTapPos,
    setAnchorTapPos,
    // Actions
    handleLocateMe,
    handleToggleFollow,
    handleToggleDropMode,
    // Iter FIELD-UX-2 — expose the raw setter so the viewer-level tool
    // ownership model can force-close drop mode when activating
    // another tool (Measure / Markup / I'm Here / etc.).
    setDropMode,
    setFollowMode,
    distanceFromMe,
  };
}
